import type { Region } from '@amp/web-app-storefronts';

export type LanguageCode = string;
export type LocalizationFilePath = string;

interface Manifest {
    properties?: {
        version: string;
        localizations: Record<LanguageCode, LocalizationFilePath>;
    };
}

export class Bundle {
    static makeBuilder(version: string, regions: Region[]): () => Bundle {
        const allLanguages: Record<string, string> = {};

        for (const region of regions) {
            for (const locale of region.locales) {
                if (typeof locale.language === 'string') {
                    const lang = locale.language.toLowerCase();
                    allLanguages[lang] = lang;
                }
            }
        }

        return () => new Bundle(version, allLanguages);
    }

    private localizations: Record<string, string> = {};

    private constructor(
        private readonly version: string,
        private readonly allLanguages: Record<string, string>,
    ) {}

    get manifest(): Manifest {
        return {
            properties: {
                version: this.version,
                localizations: {
                    // Business logic uses this list to map available languages
                    // to paths to pass to loadResource. Since we don't have a
                    // filesystem, we just return an identity mapping (and
                    // eventually disregard the path in loadResource).
                    ...this.allLanguages,
                },
            },
        };
    }

    async loadResource(_path: string): Promise<Record<string, any>> {
        // NOTE: this must return the same object every time (see below)
        return this.localizations;
    }

    setLocalizationKeys(keys: Record<string, string>) {
        // NOTE: Since most apps load localization in bootstrap before the
        //       first URL is recognized, we haven't loaded the loc strings.
        //       Thus, we have to return an empty object for the loc strings
        //       and then later add the loc in before the intent for the first
        //       page is dispatched.
        const currentProperties = Object.keys(this.localizations);
        for (const property of currentProperties) {
            delete this.localizations[property];
        }

        for (const [property, value] of Object.entries(keys)) {
            this.localizations[property] = value;
        }
    }
}
